import { gql, useApolloClient, useQuery } from '@apollo/client';
import { GET_POOLING_GROUP_CAPACITY } from '@queries/getPoolingGroupCapacity';
import { theme } from 'aimo-ui';
import DoubleBadge from 'components/common/DoubleBadge';
import FeatureFlagsContext from 'context/FeatureFlagsContext';
import useGetBenefitNameForAccessPermitPoolingGroup from 'hooks/useGetBenefitNameForAccessPermitPoolingGroup';
import useGetPoolingInfo from 'hooks/useGetPoolingInfo';
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Query,
  QueryGetPoolingGroupCapacityArgs,
} from 'types/generatedSchemaTypes';
import { updateCache } from 'utils/cacheUtils';

// Constants
const POOLING_GROUP_FRAGMENT = gql`
  fragment AccessPermitPoolingGroupCombinedInfoFragment on AccessPermitPoolingGroupCombinedInfo {
    uid
    freePoolingSpots
    lastFetch
  }
`;

interface PoolingBadgeProps {
  parkingZoneUid: string | undefined;
}

const PoolingBadge: React.FC<PoolingBadgeProps> = ({ parkingZoneUid }) => {
  const { t } = useTranslation();
  const client = useApolloClient();
  const [showRefreshButton, setShowRefreshButton] =
    React.useState<boolean>(false);

  const {
    flags: { show_pooling_spots_refresh_button_delay_milliseconds },
  } = React.useContext(FeatureFlagsContext);

  const { matchingPermit, loading, poolingGroup } =
    useGetPoolingInfo(parkingZoneUid);

  const benefitName =
    useGetBenefitNameForAccessPermitPoolingGroup(poolingGroup);
  const poolingGroupUid = poolingGroup?.uid;

  const resetRefresh = React.useCallback(() => {
    setShowRefreshButton(false);
    return setTimeout(
      () => {
        setShowRefreshButton(true);
      },
      parseInt(show_pooling_spots_refresh_button_delay_milliseconds, 10)
    );
  }, [show_pooling_spots_refresh_button_delay_milliseconds]);

  const handleAfterLoad = React.useCallback(
    (result: Query) => {
      updateCache(
        client.cache,
        {
          __typename: 'AccessPermitPoolingGroupCombinedInfo',
          uid: poolingGroupUid,
        },
        {
          fragment: POOLING_GROUP_FRAGMENT,
          data: {
            uid: poolingGroupUid,
            freePoolingSpots: result.getPoolingGroupCapacity?.free,
            lastFetch: Date.now(),
          },
        }
      );
      resetRefresh();
    },
    [client.cache, poolingGroupUid, resetRefresh]
  );

  const {
    loading: loadingPoolingGroupCapacity,
    data: freePoolingSpots,
    refetch,
  } = useQuery<Query, QueryGetPoolingGroupCapacityArgs>(
    GET_POOLING_GROUP_CAPACITY,
    {
      skip: !poolingGroupUid,
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
      variables: poolingGroupUid
        ? { poolingGroupId: poolingGroupUid }
        : undefined,
      onCompleted: handleAfterLoad,
    }
  );

  React.useEffect(() => {
    const timeout = resetRefresh();
    return () => clearTimeout(timeout);
  }, [poolingGroup?.lastFetch, resetRefresh]);

  if (!matchingPermit || matchingPermit.isWaitingActivation) {
    return null;
  }

  const handleReload = () =>
    refetch({ poolingGroupId: poolingGroupUid || undefined });

  return (
    <DoubleBadge
      bgColor={theme.colors.lightBg.lilac}
      loading={loading}
      testID={`pooling-${matchingPermit.id}`}
      iconName="Park-Filled"
      innerBadgeTextColor={
        freePoolingSpots && !loadingPoolingGroupCapacity
          ? undefined
          : theme.colors.gray300
      }
      exludeRightContent={poolingGroup?.capacityHidden || false}
      textLeft={benefitName || ' - '}
      textRight={t('parking.parkingSpotsAvailable', {
        spots: poolingGroup?.freePoolingSpots || 0,
      })}
      reloadOnPress={handleReload}
      showRefreshButton={showRefreshButton}
      loadingInnerBadge={loadingPoolingGroupCapacity}
    />
  );
};

export default PoolingBadge;
